import {
  Directive,
  Input,
  HostListener,
  EventEmitter,
  ElementRef,
  forwardRef,
  HostBinding,
  Inject,
  NgZone
} from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';
import { take } from 'rxjs/operators';

import { ValueAccessorProviderFactory } from '../../value-accessor/value-accessor-provider-factory';
import { DateTimeFormat } from '../../../services/date/date-formatter.service';

import { DatePickerService } from './date-picker.service';

import type {
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  Type
} from '@angular/core';
import type { UntypedFormControl, Validator, ControlValueAccessor, ValidationErrors } from '@angular/forms';
import type { Subscription } from 'rxjs';

import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';

import type { DatePickerComponent } from './date-picker.component';
import type { DateValidator } from './date-validator.type';

@Directive({
  selector: 'input[tdWbDatePicker]',
  providers: [
    ValueAccessorProviderFactory(DatePickerInputDirective),
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef((): Type<DatePickerInputDirective> => DatePickerInputDirective),
      multi: true
    }
  ]
})
export class DatePickerInputDirective implements ControlValueAccessor, OnDestroy, OnInit, Validator, OnChanges {

  @Input()
  get value(): Date | null { return this._value; }

  set value(value: Date | null) {
    const oldDate = this.value;
    this._value = value;
    this.onChangeCallback(value);

    if (oldDate !== value) {
      this.valueChange.emit(value);
    }
  }

  @Input()
  set tdWbDatePicker(value: DatePickerComponent) {
    if (value === undefined) {
      return;
    }

    this.datepicker = value;
    this.datepicker.registerInput(this);

    this.datepickerSubscription = this.datepicker.selectedChanged.subscribe((selected: Date): void => {
      this.writeValue(selected);
    });
    this.datepickerInputFocusSubscription = this.datepicker.inputFocusEventEmitter
      .subscribe((focus: boolean): void => {
        if (focus) {
          this.doFocus();
        }
      });
  }

  /**
   * Function which should return true if date in the selector should be disabled for selection.
   */
  @Input() disabledDates: DateValidator;

  /**
   * If true, weekends cannot be selected; defaults to true
   */
  @Input() noWeekends: boolean = true;

  /**
   * Format to parse date entered by user; defaults to yyyy-MM-dd
   */
  @Input() format: DatePickerFormatType = DateTimeFormat.YEAR_MONTH_DAY;

  /**
   * placeholder default to date format
   */
  @Input() placeholder: string;

  /**
   * Oldest selectable date
   */
  @Input() minDate: Date;

  /**
   * Latest selectable date
   */
  @Input() maxDate: Date;

  /**
   * Whether the datepicker-input is disabled
   * */
  @Input()
  get disabled(): boolean { return !!this._disabled; }

  set disabled(value: boolean) {
    const newValue = value;

    if (this._disabled !== newValue) {
      this._disabled = newValue;
      this.disabledChange.emit(newValue);
    }
  }

  /**
   * Id of input element
   * */
  @Input()
  get id(): string {
    return this.inputId;
  }

  set id(value: string) {
    this.inputId = value;
  }

  @Input()
  get required(): boolean { return !!this._required; }

  set required(value: boolean) {
    const newValue = value;

    if (this._required !== newValue) {
      this._required = newValue;
    }
  }

  @HostBinding('attr.disabled')
  get attributeDisabled(): string {
    return this._disabled ? '' : null;
  }

  @HostBinding('attr.required')
  get attributeRequired(): string {
    return this._required ? '' : null;
  }

  @HostBinding('attr.maxlength')
  get attributeMaxlength(): number {
    return this.getDateFormat().length;
  }

  @HostBinding('attr.placeholder')
  get attributePlaceholder(): string {
    const format = this.datePickerService.getPlaceholderFormat(this.getDateFormat());
    return this.placeholder ?? format.toLocaleUpperCase();
  }

  /**
   * Array of validator functions for model value
   */
  disabledDateValidators: Array<DateValidator> = [];

  valueChange: EventEmitter<Date | null> = new EventEmitter<Date | null>();
  disabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private datepicker: DatePickerComponent;
  private datepickerSubscription: Subscription;
  private datepickerInputFocusSubscription: Subscription;
  private _value: Date | null;
  private formatted: string;
  private isEmpty: boolean;
  private _disabled: boolean;
  private _required: boolean;
  private inputId: string;
  private onNgChange: () => void;

  constructor(@Inject(ElementRef) private elementRef: ElementRef<HTMLInputElement>,
              @Inject(DatePickerService) private datePickerService: DatePickerService,
              @Inject(NgZone) private ngZone: NgZone) {
  }

  /**
   * Update model on change
   *
   * @param target
   */
  @HostListener('change', ['$event.target'])
  onChange(target: EventTarget | null): void {
    const text = (target as HTMLInputElement)?.value;
    this.isEmpty = text.length === 0;
    this.setValue(this.parseStringToDate(text), false);
  }

  /**
   * Update model on input
   *
   * @param target
   */
  @HostListener('input', ['$event.target'])
  onInput(target: EventTarget | null): void {
    const text = (target as HTMLInputElement)?.value;
    this.isEmpty = text.length === 0;
    this.parser(text);
  }

  ngOnInit(): void {
    this.setValidators();
  }

  ngOnDestroy(): void {
    this.datepickerSubscription.unsubscribe();
    this.datepickerInputFocusSubscription.unsubscribe();
    this.valueChange.complete();
    this.disabledChange.complete();
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onNgChange = fn;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes !== undefined && this.onNgChange !== undefined) {
      this.onNgChange();
    }
  }

  // Implemented as part of ControlValueAccessor.
  writeValue(value: Date): void {
    this.isEmpty = !this.datePickerService.isDate(value);
    this.setValue(value);
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(): void {
  }

  /**
   * Validator function for date picker
   *
   * @param formControl The date picker form control
   * @returns Returns validation result
   */
  validate(formControl: UntypedFormControl):  ValidationErrors | null {
    if (this.isEmpty && this.required) {
      return {
        required: true
      };
    }
    if (!this.isEmpty && this.getParsed() === null) {
      return {
        parse: true
      };
    }
    if (
      this.datePickerService.isDate(this.minDate) &&
      this.getParsed() !== null &&
      this.datePickerService.validateMinDate(this.minDate)(formControl.value)
    ) {
      return {
        minDate: true
      };
    }
    if (
      this.datePickerService.isDate(this.maxDate) &&
      this.getParsed() !== null &&
      this.datePickerService.validateMaxDate(this.maxDate)(formControl.value)
    ) {
      return {
        maxDate: true
      };
    }
    if (this.getParsed() !== null &&
      this.isDisabledDate(formControl.value) &&
      !this.datePickerService.validateNoWeekends()(formControl.value)
    ) {
      return {
        noWeekends: true
      };
    }
    if (
      this.getParsed() !== null &&
      this.isDisabledDate(formControl.value)
    ) {
      return {
        disabledDate: true
      };
    }
    return null;
  }

  /**
   * This function checks if a date is disabled
   *
   * @param date String to convert to Date object
   * @returns Returns true if date is disabled
   */
  isDisabledDate(date: Date): boolean {
    return this.datePickerService.isDisabledDate(date, this.disabledDates, this.disabledDateValidators);
  }

  /**
   * Returns the date format
   *
   * @returns date format
   */
  getDateFormat(): DatePickerFormatType {
    return this.datePickerService.getDateFormat(this.format);
  }

  private onChangeCallback: (_: unknown) => void =  (): void => {};

  /**
   * Sets ngModel value for datepicker
   *
   * @param date value to set ngModel value
   * @param setToDefault if true, sets ngModel value to null and formatted value to empty string
   */
  private setValue(date: Date, setToDefault: boolean = true): void {
    if (date !== undefined && this.datePickerService.isDate(date)) {
      this.setDateValue(date);
    } else if (this.isEmpty) {
      this.setEmptyValue();
    } else {
      this.setToDefaultValue(setToDefault);
    }
  }

  /**
   * This function parses string to date if valid. Otherwise returns null
   *
   * @param text String to convert to Date object
   * @returns Returns converted Date object
   */
  private parseStringToDate(text: string): Date | undefined {
    let result: Date;
    let valid: boolean;
    let date: Date;
    if (text.length !== 0) {
      valid = this.datePickerService.validateDatePattern(this.format, text);
      if (valid) {
        date = this.datePickerService.parseDate(text, this.format);
        if (date !== null) {
          result = date;
        }
      }
    }
    return result;
  }

  /**
   * Adds necessary validators for date picker
   */
  private setValidators(): void {
    if (this.noWeekends) {
      this.registerDisabledDateValidator(this.datePickerService.validateNoWeekends());
    }
  }

  /**
   * Register validators to disabledDateValidators
   *
   * @param disabledDateValidator Function to add for validation
   */
  private registerDisabledDateValidator(disabledDateValidator: DateValidator): void {
    this.disabledDateValidators.push(disabledDateValidator);
  }

  /**
   * This getter returns parsed model value.
   *
   * @returns Returns parsed model value or null
   */
  private getParsed(): Date | null {
    return this.datePickerService.isDate(this.value) ? this.value : null;
  }

  private doFocus(): void {
    this.ngZone.onStable.asObservable().pipe(take(1)).subscribe((): void => {
      this.elementRef.nativeElement.focus();
    });
  }

  /**
   * This function is parser for the text input element
   */
  private parser(text: string): void {
    const date = this.parseStringToDate(text);
    if (date !== undefined) {
      this.setDateValue(date);
    } else if (this.isEmpty) {
      this.setEmptyValue();
    }
  }

  private setDateValue(date: Date): void {
    this.value = date;
    this.formatted = this.datePickerService.formatDate(this.value, this.format);
    this.elementRef.nativeElement.value = this.formatted;
  }

  private setEmptyValue(): void {
    this.value = undefined;
    this.formatted = '';
    this.elementRef.nativeElement.value = this.formatted;
  }

  private setToDefaultValue(setToDefault: boolean = true): void {
    this.value = null;
    if (setToDefault) {
      this.formatted = '';
      this.elementRef.nativeElement.value = this.formatted;
    }
  }
}
